import { FC, useContext, useState } from 'react';
import { usePlaybooksView } from '../hooks/use-playbooks-view';
import { Button } from '@shadcn/ui/components/ui/button';
import { Edit, Plus, Trash } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@shadcn/ui/components/ui/avatar';
import { AlertDialog } from '@shadcn/ui/components/ui/alert-dialog';
import { ModalNavigationContext } from './playbooks-modal';

export const PlaybooksGrid: FC = () => {
    const { playbooks, deletePlaybook } = usePlaybooksView();
    const modalNavigation = useContext(ModalNavigationContext);
    const [playbookToDelete, setPlaybookToDelete] = useState<{ id: string; name: string } | null>(null);

    const handleEditPlaybook = (playbookId: string) => {
        if (modalNavigation) {
            modalNavigation.navigateToPlaybook(playbookId);
        }
    };

    const handleCreatePlaybook = () => {
        if (modalNavigation) {
            modalNavigation.navigateToNew();
        }
    };

    const handleDeletePlaybook = (playbookId: string, playbookName: string) => {
        setPlaybookToDelete({ id: playbookId, name: playbookName });
    };

    const confirmDelete = () => {
        if (playbookToDelete) {
            deletePlaybook(playbookToDelete.id);
            setPlaybookToDelete(null);
        }
    };

    return (
        <>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold">Playbooks</h1>
                </div>
                <div className="space-y-2">
                    {/* Create New Playbook Row */}
                    <div
                        className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={handleCreatePlaybook}
                    >
                        <Avatar className="h-8 w-8 border-2 border-dashed border-muted-foreground/30">
                            <AvatarFallback style={{ backgroundColor: 'hsl(var(--brand-100))' }}>
                                <Plus className="h-4 w-4 text-muted-foreground" />
                            </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-muted-foreground">Create new playbook</span>
                    </div>

                    {/* Playbook List */}
                    {playbooks.map((playbook, index) => (
                        <div
                            key={playbook.id}
                            id={`playbook-row-${index}`}
                            className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Avatar className="h-8 w-8 shrink-0">
                                    <AvatarImage src={playbook.imgSrc || undefined} alt={playbook.name} />
                                    <AvatarFallback className="text-xs font-medium">
                                        {playbook.name?.charAt(0)?.toUpperCase() || 'P'}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{playbook.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {(playbook as any)?._GroupToUserContacts?.length || 0} contacts
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditPlaybook(playbook.id)}
                                    className="shrink-0"
                                >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleDeletePlaybook(playbook.id, playbook.name)}
                                    className="shrink-0"
                                >
                                    <Trash className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <AlertDialog open={!!playbookToDelete} onOpenChange={() => setPlaybookToDelete(null)}>
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Title>
                            Are you sure you want to delete "{playbookToDelete?.name}"?
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                            This action cannot be undone. This will permanently delete the playbook and remove it from
                            all contacts.
                        </AlertDialog.Description>
                    </AlertDialog.Header>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog>
        </>
    );
};
